/**
 * explorerExport.js — Explorer "Export" helpers (plan §2.2–2.5).
 *
 * Pure filename/collision/planning helpers plus the browser-side IO used by the
 * directory-picker export: File System Access writes, attachment fetches, and
 * the MP3 transcode pipeline (decode on the main thread, encode in a worker).
 *
 * Row assembly stays in ExploreTab.js (it needs that component's in-scope cell
 * helpers); this module owns everything downstream of an assembled row so the
 * CSV cell paths and the actually-written files are decided in ONE place.
 */

import { getAudioSignedUrl, getFileSignedUrl } from './cloudApi';

// --- Pure name helpers -----------------------------------------------------

/**
 * Render a timestamp at second granularity, e.g. 20260707160549. Local time, to
 * match synthesizeAnonymousHandle (ExploreTab) which is also local. Empty string
 * for an unparseable input.
 */
export function formatTimestampCompact(ts) {
    const d = ts ? new Date(ts) : null;
    if (!d || Number.isNaN(d.getTime())) return '';
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
}

/** Strip characters that are illegal in file/dir names on common filesystems. */
export function sanitizeFilenamePart(s) {
    return String(s == null ? '' : s)
        .replace(/[/\\:*?"<>|]+/g, '_')
        .replace(/\s+/g, ' ')
        .trim();
}

/** `<surveyName>_<YYYY-MM-DD>` (plan §2.2). Local date. */
export function buildFolderName(surveyName, date = new Date()) {
    const p = (n) => String(n).padStart(2, '0');
    const ymd = `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
    const name = sanitizeFilenamePart(surveyName) || 'export';
    return `${name}_${ymd}`;
}

/** `<handle>_<responseTimestamp>_<surveyName>.mp3` (plan §2.5). */
export function buildAudioBaseName({ handle, timestamp, surveyName }) {
    const parts = [
        sanitizeFilenamePart(handle) || 'anonymous',
        formatTimestampCompact(timestamp) || 'unknown',
        sanitizeFilenamePart(surveyName) || 'survey',
    ];
    return `${parts.join('_')}.mp3`;
}

/** Split a filename into [base, ext] where ext includes the leading dot (or ''). */
function splitExt(name) {
    const i = name.lastIndexOf('.');
    if (i <= 0) return [name, ''];
    return [name.slice(0, i), name.slice(i)];
}

/**
 * Collision-avoiding namer. Returns a function that, given a desired filename,
 * yields a unique one within this namer's scope by appending `_2`, `_3`, …
 * before the extension. Case-insensitive tracking (macOS/Windows filesystems).
 */
export function makeUniqueNamer() {
    const used = new Set();
    return function assign(desired) {
        const clean = sanitizeFilenamePart(desired) || 'file';
        const [base, ext] = splitExt(clean);
        let candidate = clean;
        let n = 2;
        while (used.has(candidate.toLowerCase())) {
            candidate = `${base}_${n}${ext}`;
            n += 1;
        }
        used.add(candidate.toLowerCase());
        return candidate;
    };
}

/**
 * Classify an audio source. MP3 passes through untouched; WebM/WAV (and anything
 * else) is transcoded (plan §2.5). Accepts a path and/or a content-type.
 */
export function detectAudioKind(pathOrType = '', contentType = '') {
    const p = String(pathOrType).toLowerCase();
    const ct = String(contentType).toLowerCase();
    if (ct.includes('mpeg') || ct.includes('mp3') || /\.mp3(\?|$)/.test(p)) return 'mp3';
    return 'transcode';
}

// --- Planning (pure) -------------------------------------------------------

/**
 * Assign a final, unique `audio/<name>.mp3` path to each clip, deduplicating by
 * source gcs path so one audio file is written (and named) exactly once. The
 * returned `byGcs` map is what the Results CSV Voice cell reads, guaranteeing the
 * CSV path and the written file agree.
 *
 * @param {{gcsPath:string, handle:string, timestamp:any, questionText:string, surveyName:string}[]} clips
 * @returns {{ordered:{gcsPath,name,relPath,handle,questionText,timestamp}[], byGcs:Map<string,string>}}
 */
export function planAudioClips(clips, surveyName) {
    const namer = makeUniqueNamer();
    const byGcs = new Map();
    const ordered = [];
    for (const c of clips || []) {
        if (!c || !c.gcsPath) continue;
        if (byGcs.has(c.gcsPath)) continue;
        const name = namer(buildAudioBaseName({ handle: c.handle, timestamp: c.timestamp, surveyName }));
        const relPath = `audio/${name}`;
        byGcs.set(c.gcsPath, relPath);
        ordered.push({ gcsPath: c.gcsPath, name, relPath, handle: c.handle, questionText: c.questionText, timestamp: c.timestamp });
    }
    return { ordered, byGcs };
}

/**
 * Assign a final, unique `files/<originalName>` path to each upload, dedup by gcs
 * path. `byGcs` feeds the Results CSV Upload cell so the cell path matches the
 * written file.
 *
 * @param {{gcsPath:string, filename:string}[]} uploads
 * @returns {{ordered:{gcsPath,name,relPath,filename}[], byGcs:Map<string,string>}}
 */
export function planUploads(uploads) {
    const namer = makeUniqueNamer();
    const byGcs = new Map();
    const ordered = [];
    for (const u of uploads || []) {
        if (!u || !u.gcsPath) continue;
        if (byGcs.has(u.gcsPath)) continue;
        const original = u.filename || decodeURIComponent(String(u.gcsPath).split('/').pop() || 'file');
        const name = namer(original);
        const relPath = `files/${name}`;
        byGcs.set(u.gcsPath, relPath);
        ordered.push({ gcsPath: u.gcsPath, name, relPath, filename: original });
    }
    return { ordered, byGcs };
}

// --- File System Access IO -------------------------------------------------

/** True when the directory-picker API is available (Chrome/Edge). */
export function directoryPickerSupported() {
    return typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';
}

export async function pickDirectory() {
    return window.showDirectoryPicker();
}

export async function ensureSubdir(dirHandle, name) {
    return dirHandle.getDirectoryHandle(name, { create: true });
}

export async function writeFileToDir(dirHandle, name, data) {
    const fileHandle = await dirHandle.getFileHandle(name, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(data);
    await writable.close();
}

// --- Attachment fetch (plan §2.4) ------------------------------------------

/**
 * Fetch each planned upload's bytes (signed URL → blob) and write it into the
 * `files/` directory. Returns a map keyed by source gcs path carrying the ACTUAL
 * fetched byte length (no metadata round-trip) so the CSV Value cell shows a real
 * size and never a blank.
 *
 * @returns {Promise<Map<string,{name:string,bytes:number}>>}
 */
export async function exportUploads(filesDir, orderedUploads, { onProgress } = {}) {
    const result = new Map();
    let done = 0;
    const total = orderedUploads.length;
    for (const u of orderedUploads) {
        try {
            const url = await getFileSignedUrl(u.gcsPath, u.filename);
            const resp = await fetch(url);
            const buf = await resp.arrayBuffer();
            await writeFileToDir(filesDir, u.name, buf);
            result.set(u.gcsPath, { name: u.name, bytes: buf.byteLength });
        } catch (err) {
            console.error('[export] upload failed:', u.gcsPath, err);
        }
        done += 1;
        onProgress?.({ phase: 'files', done, total });
    }
    return result;
}

// --- Audio transcode pipeline (plan §2.5) ----------------------------------

let _audioCtx = null;
function getAudioContext() {
    if (!_audioCtx) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        _audioCtx = new Ctx();
    }
    return _audioCtx;
}

// One long-lived worker per export run, request/response matched by id.
function createEncoder() {
    const worker = new Worker(new URL('../workers/mp3Encoder.worker.js', import.meta.url));
    let nextId = 1;
    const pending = new Map();
    worker.onmessage = (e) => {
        const { id, type, mp3, error, ratio } = e.data || {};
        const entry = pending.get(id);
        if (!entry) return;
        if (type === 'progress') { entry.onProgress?.(ratio); return; }
        if (type === 'done') { pending.delete(id); entry.resolve(new Uint8Array(mp3)); return; }
        if (type === 'error') { pending.delete(id); entry.reject(new Error(error || 'encode failed')); }
    };
    worker.onerror = (e) => {
        const err = new Error(e.message || 'worker error');
        for (const entry of pending.values()) entry.reject(err);
        pending.clear();
    };
    return {
        encode(channels, sampleRate, onProgress) {
            const id = nextId++;
            return new Promise((resolve, reject) => {
                pending.set(id, { resolve, reject, onProgress });
                // Copy each channel into its own transferable buffer.
                const copies = channels.map((ch) => {
                    const copy = new Float32Array(ch.length);
                    copy.set(ch);
                    return copy;
                });
                worker.postMessage(
                    { id, channels: copies, sampleRate },
                    copies.map((c) => c.buffer)
                );
            });
        },
        terminate() { worker.terminate(); },
    };
}

/**
 * Fetch → (passthrough | decode+encode) → write each planned clip into `audio/`.
 * MP3 sources are written untouched; WebM/WAV are decoded and re-encoded to
 * 64 kbps mono MP3 in the worker. Returns a map keyed by source gcs path with the
 * clip duration (seconds, or null when unknown) for the playlist.
 *
 * @returns {Promise<Map<string,{name:string,durationSeconds:(number|null)}>>}
 */
export async function exportAudioClips(audioDir, orderedClips, { onProgress } = {}) {
    const result = new Map();
    if (!orderedClips.length) return result;
    const encoder = createEncoder();
    let done = 0;
    const total = orderedClips.length;
    try {
        for (const clip of orderedClips) {
            const clipIndex = done; // captured for the progress closure below
            let durationSeconds = null;
            try {
                const url = await getAudioSignedUrl(clip.gcsPath);
                const resp = await fetch(url);
                const contentType = resp.headers.get('content-type') || '';
                const arrayBuffer = await resp.arrayBuffer();
                const kind = detectAudioKind(clip.gcsPath, contentType);
                if (kind === 'mp3') {
                    // Passthrough — no decode/encode. Duration unknown → null (-1 in M3U).
                    await writeFileToDir(audioDir, clip.name, arrayBuffer);
                } else {
                    const audioBuffer = await getAudioContext().decodeAudioData(arrayBuffer.slice(0));
                    durationSeconds = audioBuffer.duration;
                    const channels = [];
                    for (let c = 0; c < audioBuffer.numberOfChannels; c += 1) {
                        channels.push(audioBuffer.getChannelData(c));
                    }
                    const mp3Bytes = await encoder.encode(channels, audioBuffer.sampleRate, (ratio) => {
                        onProgress?.({ phase: 'audio', done: clipIndex, total, ratio });
                    });
                    await writeFileToDir(audioDir, clip.name, mp3Bytes);
                }
                result.set(clip.gcsPath, { name: clip.name, durationSeconds });
            } catch (err) {
                console.error('[export] audio clip failed:', clip.gcsPath, err);
                result.set(clip.gcsPath, { name: clip.name, durationSeconds: null });
            }
            done += 1;
            onProgress?.({ phase: 'audio', done, total });
        }
    } finally {
        encoder.terminate();
    }
    return result;
}
